import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ArrowRight,
  Phone,
  MapPin,
  Clock,
  Hospital,
  Briefcase,
  Droplet,
  HeartPulse,
  Repeat,
  Users,
  Building2,
  AlertTriangle,
  Accessibility,
  Route,
} from 'lucide-react';

const CANONICAL_PATH = '/missouri-city-tx-medical-transportation';

const MC_FAQS = [
  {
    question: 'Is Missouri City part of MoveraCare’s regular service area?',
    answer:
      "Yes. Missouri City sits directly adjacent to MoveraCare’s home city of Richmond in Fort Bend County. Missouri City is inside our regular non-emergency medical transportation coverage, including the Fort Bend County portion and, subject to scheduling, the Harris County portion of the city.",
  },
  {
    question: 'Missouri City spans two counties — do you cover both sides?',
    answer:
      "Yes. Missouri City has a Fort Bend County portion (Sienna, Riverstone-Missouri City side, Quail Valley, Vicksburg, Colony Lakes, Colony Bend) and a Harris County portion (Fort Bend Houston, Fondren SW area). MoveraCare’s core operating area is Fort Bend County, and Harris County trips within our service area are supported. Cross-county trips are common.",
  },
  {
    question: 'What Missouri City neighborhoods do you serve?',
    answer:
      "MoveraCare serves Missouri City broadly, including Sienna (formerly Sienna Plantation), Riverstone (Missouri City side), Quail Valley, Vicksburg, Colony Lakes, Colony Bend, Lake Olympia, Hunters Glen, Plantation Bend, and the Fort Bend Houston area east of the Fort Bend / Harris County line. Service is scheduled subject to availability.",
  },
  {
    question: 'Do you provide recurring dialysis transportation in Missouri City?',
    answer:
      "Yes. Standing weekly schedules are supported for Missouri City dialysis patients — Monday/Wednesday/Friday, Tuesday/Thursday/Saturday, or other recurring plans coordinated with the treating dialysis center. Wait-and-return coordination can be arranged in advance.",
  },
  {
    question: 'Can Missouri City hospitals and SNFs request discharge transportation?',
    answer:
      "Yes. Missouri City-area discharge planners, case managers, and social workers can request non-emergency discharge transportation from a hospital or skilled nursing facility to home, rehabilitation, or assisted living. Same-day and scheduled discharges are supported for medically stable patients based on availability.",
  },
  {
    question: 'How do I book a wheelchair-accessible ride in Missouri City?',
    answer:
      "Call dispatch at (832) 925-2128 or use the online Book a Ride form. Family members, case managers, or facility staff arranging a Missouri City ride on behalf of a patient can use the Request Patient Transportation form.",
  },
  {
    question: "Do you accept Workers' Compensation authorizations for Missouri City injured workers?",
    answer:
      "Yes. MoveraCare accepts authorized non-emergency transportation for Missouri City-area injured workers traveling to Workers' Compensation-approved medical, therapy, IME, FCE, and occupational medicine appointments — including trips into Sugar Land, Richmond, Katy, or Houston where the authorized provider is located. Authorization is determined by the applicable adjuster, case manager, TPA, or payer.",
  },
  {
    question: 'How far in advance should Missouri City riders schedule?',
    answer:
      "For routine Missouri City trips we recommend 24–48 hours notice. Recurring dialysis, therapy, and rehabilitation runs should be established as a standing schedule. Same-day requests may be considered when vehicle and driver availability allows — same-day acceptance is not guaranteed.",
  },
];

const MC_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'MedicalBusiness',
  '@id': 'https://www.moveracaremobility.com/missouri-city-tx-medical-transportation#business',
  name: 'MoveraCare Mobility — Missouri City, TX Non-Emergency Medical Transportation',
  legalName: 'MoveraCare Services LLC',
  description:
    "Richmond, TX-based non-emergency medical transportation provider serving Missouri City across its Fort Bend County and Harris County portions — wheelchair and ambulatory NEMT for medical, dialysis, discharge, therapy, and Workers' Compensation trips.",
  url: 'https://www.moveracaremobility.com/missouri-city-tx-medical-transportation',
  telephone: '+1-832-925-2128',
  email: 'dispatch@moveracaremobility.com',
  areaServed: [
    { '@type': 'City', name: 'Missouri City, TX' },
    { '@type': 'AdministrativeArea', name: 'Fort Bend County, Texas' },
    { '@type': 'AdministrativeArea', name: 'Harris County, Texas' },
  ],
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Richmond',
    addressRegion: 'TX',
    addressCountry: 'US',
  },
  openingHoursSpecification: [
    { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'], opens: '06:00', closes: '19:00' },
    { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Sunday'], opens: '12:00', closes: '17:00' },
  ],
};

const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: MC_FAQS.map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: { '@type': 'Answer', text: f.answer },
  })),
};

const SERVICE_TYPES = [
  { icon: Accessibility, title: 'Wheelchair Transportation', desc: 'Wheelchair-accessible transportation across Missouri City.', to: '/services/wheelchair-transportation' },
  { icon: Users, title: 'Ambulatory Transportation', desc: 'Comfortable ambulatory transport for Missouri City patients.', to: '/services/ambulatory-transportation' },
  { icon: Hospital, title: 'Hospital Discharge Transportation', desc: 'Same-day and scheduled discharge transportation from Missouri City-area hospitals and SNFs.', to: '/services/hospital-discharge' },
  { icon: Droplet, title: 'Dialysis Transportation', desc: 'Recurring standing schedules for Missouri City dialysis patients.', to: '/services/dialysis-transportation' },
  { icon: HeartPulse, title: 'Therapy & Rehabilitation Transportation', desc: 'Physical therapy, occupational therapy, and rehabilitation appointment transportation.', to: '/services/therapy-rehabilitation-transportation' },
  { icon: Briefcase, title: "Workers' Compensation Transportation", desc: 'Authorized transportation for Missouri City-area injured workers.', to: '/healthcare-partners/workers-compensation' },
  { icon: Repeat, title: 'Recurring Medical Transportation', desc: 'Standing NEMT schedules for Missouri City patients with ongoing treatment plans.', to: '/services/recurring-medical-transportation' },
  { icon: Clock, title: 'Wait-and-Return Transportation', desc: 'Driver-waiting service for shorter Missouri City appointments when arranged in advance.', to: '/services' },
];

const MC_AREAS = [
  'Sienna',
  'Riverstone (Missouri City side)',
  'Quail Valley',
  'Vicksburg',
  'Colony Lakes',
  'Colony Bend',
  'Lake Olympia',
  'Hunters Glen',
  'Plantation Bend',
  'Palmer Plantation',
  'Meadowcreek',
  'Fifth Street corridor',
  'Highway 6 corridor',
  'FM 1092 (Murphy Rd) corridor',
  'Fort Bend Toll Road access',
  'Fort Bend Houston (Harris County side)',
];

const NEARBY_LINKS = [
  { name: 'Richmond', to: '/richmond-tx-medical-transportation', note: 'MoveraCare’s home base — adjacent Fort Bend County coverage.' },
  { name: 'Sugar Land', to: '/sugar-land-tx-medical-transportation', note: 'Frequent Missouri City \u2194 Sugar Land medical trips.' },
  { name: 'Katy', to: '/katy-tx-medical-transportation', note: 'Cross-county coverage into Fort Bend/Harris/Waller.' },
  { name: 'Houston', to: '/houston-tx-medical-transportation', note: 'Trips into Houston and the Texas Medical Center.' },
];

const MissouriCityMedicalTransportation = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="missouri-city-page">
      <Seo
        title="Medical Transportation Missouri City TX | Wheelchair &amp; Ambulatory NEMT | MoveraCare"
        description="Medical transportation Missouri City TX. MoveraCare Mobility (based in Richmond, TX) provides wheelchair and ambulatory non-emergency medical transportation across Missouri City's Fort Bend and Harris County portions. Call 832-925-2128."
        path={CANONICAL_PATH}
        schema={[MC_SCHEMA, FAQ_SCHEMA]}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Service Areas', url: '/services' },
          { name: 'Missouri City, TX', url: CANONICAL_PATH },
        ]}
      />

      {/* Hero */}
      <section className="py-16 md:py-20 bg-[#004C97]" data-testid="missouri-city-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="text-sm text-white/80 mb-6" data-testid="breadcrumbs">
            <ol className="flex flex-wrap items-center gap-1">
              <li><Link to="/" className="hover:text-[#F9A825]">Home</Link></li>
              <li className="text-white/50">/</li>
              <li><Link to="/services" className="hover:text-[#F9A825]">Service Areas</Link></li>
              <li className="text-white/50">/</li>
              <li className="text-[#F9A825] font-semibold">Missouri City, TX</li>
            </ol>
          </nav>
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-[#F9A825] text-[#004C97] px-4 py-1.5 rounded-full text-sm font-bold mb-6">
              <MapPin className="w-4 h-4" />
              MISSOURI CITY · FORT BEND &amp; HARRIS COUNTY, TX
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Medical Transportation in Missouri City, Texas
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              MoveraCare Mobility is based in Richmond, TX and provides professional wheelchair and ambulatory non-emergency medical transportation — including hospital discharge, dialysis, and Workers&apos; Compensation trips — for Missouri City patients, families, healthcare facilities, and case managers across both the Fort Bend County and Harris County portions of the city.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              <Link to="/book">
                <Button size="lg" className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold text-lg px-8 shadow-lg" data-testid="missouri-city-cta-book">
                  Book a Ride
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/request-patient-transportation">
                <Button size="lg" className="bg-white text-[#004C97] hover:bg-gray-100 font-semibold text-lg px-8 shadow-lg" data-testid="missouri-city-cta-refer">
                  Refer a Patient
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:+18329252128">
                <Button size="lg" variant="outline" className="border-2 border-[#F9A825] text-[#F9A825] hover:bg-[#F9A825] hover:text-[#004C97] font-semibold text-lg px-8" data-testid="missouri-city-cta-call">
                  <Phone className="mr-2 w-5 h-5" />
                  Call Dispatch (832) 925-2128
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-14 bg-white" data-testid="missouri-city-overview">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">MISSOURI CITY NEMT OVERVIEW</p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-6 leading-tight">
            Missouri City NEMT — A Two-County Neighbor at Our Doorstep
          </h2>
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
            <p>
              Missouri City is one of MoveraCare Mobility&apos;s closest neighbors. The city sits immediately east of our home base in Richmond, sharing the Fort Bend County landscape and stretching across the county line into a smaller Harris County section. That two-county geography is one of the things residents don&apos;t always think about — until an appointment, a discharge, or a Workers&apos; Compensation authorization sends them across the line.
            </p>
            <p>
              The Missouri City service area is a mix of established neighborhoods like Quail Valley, Colony Lakes, and Lake Olympia and newer, fast-growing master-planned communities like Sienna and the Missouri City portion of Riverstone. Each generates its own kind of NEMT demand: recurring dialysis runs, discharge coordination from Fort Bend County hospitals, physical therapy schedules, and standing rides for seniors aging in place. Missouri City patients also frequently travel toward <Link to="/sugar-land-tx-medical-transportation" className="text-[#F9A825] font-semibold hover:underline">Sugar Land</Link>, <Link to="/richmond-tx-medical-transportation" className="text-[#F9A825] font-semibold hover:underline">Richmond</Link>, and into <Link to="/houston-tx-medical-transportation" className="text-[#F9A825] font-semibold hover:underline">Houston</Link> — including the Texas Medical Center — for specialty care.
            </p>
            <p>
              MoveraCare’s Missouri City services include wheelchair transportation using wheelchair-accessible vehicles, comfortable ambulatory transport, recurring dialysis and therapy transportation, hospital and SNF discharge coordination, and authorized Workers&apos; Compensation transportation. All services are provided for medically stable passengers and are scheduled subject to vehicle and driver availability. Extended-distance assignments may require advance authorization. MoveraCare is a non-emergency medical transportation provider and does not provide ambulance, 911, stretcher ambulance, oxygen therapy, or clinical monitoring services. For medical emergencies, call 911.
            </p>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-14 bg-gray-50" data-testid="missouri-city-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">SERVICES OFFERED IN MISSOURI CITY</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] leading-tight">
              Missouri City Non-Emergency Medical Transportation Services
            </h2>
            <p className="text-gray-700 mt-3 max-w-3xl">
              Every core MoveraCare service is available for Missouri City passengers. Explore each service below, or visit the full <Link to="/services" className="text-[#F9A825] font-semibold hover:underline">Services</Link> page.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {SERVICE_TYPES.map((s) => {
              const Icon = s.icon;
              return (
                <Link key={s.title} to={s.to} className="group" data-testid={`missouri-city-service-${s.title.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}>
                  <Card className="h-full border-2 hover:border-[#F9A825] transition-all">
                    <CardHeader>
                      <div className="w-11 h-11 rounded-lg bg-[#F9A825] flex items-center justify-center mb-2">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <CardTitle className="text-[#004C97] text-base leading-tight">{s.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 leading-relaxed mb-2">{s.desc}</p>
                      <span className="text-sm font-semibold text-[#F9A825] group-hover:text-[#E08E1E]">Explore →</span>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Wheelchair & Ambulatory */}
      <section className="py-14 bg-white" data-testid="missouri-city-wheelchair-ambulatory">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">WHEELCHAIR TRANSPORTATION</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#004C97] mb-4 leading-tight">
                Missouri City Wheelchair-Accessible Transportation
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Missouri City&apos;s mix of established neighborhoods and newer master-planned communities produces steady demand for wheelchair-accessible non-emergency transportation. MoveraCare operates wheelchair-accessible vehicles; drivers assist passengers during transport. Whether the pickup is a driveway in Quail Valley, a covered entry at an assisted-living community in Sienna, or a Fort Bend Houston address on the Harris County side, wheelchair securement follows standard procedure and pickup logistics are coordinated in advance. Please share the wheelchair type, weight, and any equipment needs (including oxygen) when booking so dispatch can confirm we can safely serve the trip.
              </p>
              <Link to="/services/wheelchair-transportation" className="inline-flex items-center text-sm font-semibold text-[#F9A825] hover:text-[#E08E1E]">
                Learn about Wheelchair Transportation <ArrowRight className="ml-1 w-4 h-4" />
              </Link>
            </div>
            <div>
              <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">AMBULATORY TRANSPORTATION</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#004C97] mb-4 leading-tight">
                Missouri City Ambulatory Medical Transportation
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                For Missouri City patients who can walk to and from the vehicle independently or with limited assistance, MoveraCare provides comfortable ambulatory transport for medical, dental, therapy, and specialty appointments. Common Missouri City runs include cardiology and orthopedic visits, diagnostic imaging, primary-care follow-ups, and specialty appointments in Sugar Land, Richmond, or the Texas Medical Center. One-way, round-trip, and wait-and-return options are supported when scheduled in advance.
              </p>
              <Link to="/services/ambulatory-transportation" className="inline-flex items-center text-sm font-semibold text-[#F9A825] hover:text-[#E08E1E]">
                Learn about Ambulatory Transportation <ArrowRight className="ml-1 w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Two-County Missouri City */}
      <section className="py-14 bg-gray-50" data-testid="missouri-city-two-county">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div>
              <div className="w-14 h-14 rounded-lg bg-[#F9A825] flex items-center justify-center mb-3">
                <Route className="w-7 h-7 text-white" />
              </div>
              <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">TWO-COUNTY MISSOURI CITY</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#004C97] leading-tight">
                One City, Two Counties — Coverage on Both Sides
              </h2>
            </div>
            <div className="lg:col-span-2">
              <p className="text-gray-700 leading-relaxed mb-4">
                A Missouri City address can be in Fort Bend County or Harris County depending on where the neighborhood sits along the county line. That matters for medical transportation because insurer networks, authorized Workers&apos; Compensation providers, and hospital referral patterns often follow county boundaries.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Fort Bend County is MoveraCare&apos;s core operating area, and most of Missouri City sits inside it. Harris County trips within our regular service area are supported, including runs into the Fort Bend Houston area, and cross-county trips (Missouri City to Sugar Land, Richmond, Katy, or Houston) are part of our routine work.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Healthcare Organizations */}
      <section className="py-14 bg-[#004C97]" data-testid="missouri-city-healthcare-org">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">FOR MISSOURI CITY HEALTHCARE ORGANIZATIONS</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
              Transportation for Missouri City Hospitals, SNFs, Dialysis Centers &amp; Case Managers
            </h2>
            <p className="text-gray-200 mt-3 max-w-3xl">
              MoveraCare works directly with Missouri City-area hospitals, skilled nursing facilities, rehabilitation centers, dialysis centers, case managers, discharge planners, and social workers. Visit <Link to="/healthcare-partners" className="text-[#F9A825] font-semibold hover:underline">Healthcare Partners</Link> for the full program list.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5">
              <h3 className="text-white font-semibold text-lg mb-2">Missouri City discharge coordination</h3>
              <p className="text-gray-200 text-sm leading-relaxed">Same-day and scheduled discharge transportation from Missouri City-area hospitals and SNFs to home, rehabilitation, or assisted living for medically stable patients.</p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5">
              <h3 className="text-white font-semibold text-lg mb-2">Recurring dialysis transportation</h3>
              <p className="text-gray-200 text-sm leading-relaxed">Standing weekly schedules for Missouri City dialysis patients coordinated with the treating dialysis center.</p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5">
              <h3 className="text-white font-semibold text-lg mb-2">Therapy &amp; rehabilitation runs</h3>
              <p className="text-gray-200 text-sm leading-relaxed">Physical therapy, occupational therapy, and rehabilitation appointment transportation across Missouri City and adjacent Fort Bend / Harris County destinations.</p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5">
              <h3 className="text-white font-semibold text-lg mb-2">Case-manager coordination</h3>
              <p className="text-gray-200 text-sm leading-relaxed">Direct dispatch contact for Missouri City-area case managers, coordinators, and social workers arranging authorized non-emergency transportation.</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Link to="/healthcare-partners">
              <Button size="lg" className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold" data-testid="missouri-city-cta-healthcare-partners">Healthcare Partner Programs<ArrowRight className="ml-2 w-5 h-5" /></Button>
            </Link>
            <Link to="/healthcare-partners/hospital-discharge">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#004C97] font-semibold">Hospital Discharge</Button>
            </Link>
            <Link to="/healthcare-partners/dialysis-centers">
              <Button size="lg" variant="outline" className="border-2 border-[#F9A825] text-[#F9A825] hover:bg-[#F9A825] hover:text-[#004C97] font-semibold">Dialysis Centers</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Workers' Comp */}
      <section className="py-14 bg-white" data-testid="missouri-city-workers-comp">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            <div>
              <div className="w-14 h-14 rounded-lg bg-[#F9A825] flex items-center justify-center mb-3">
                <Briefcase className="w-7 h-7 text-white" />
              </div>
              <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">WORKERS&apos; COMPENSATION</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#004C97] leading-tight">
                Missouri City Workers&apos; Compensation Transportation
              </h2>
            </div>
            <div className="lg:col-span-2">
              <p className="text-gray-700 leading-relaxed mb-4">
                MoveraCare provides non-emergency transportation for Missouri City-area injured workers attending Workers&apos; Compensation-authorized medical, therapy, IME, FCE, occupational medicine, and diagnostic appointments — often across county lines when the authorized provider is located outside Missouri City. We coordinate with adjusters, nurse case managers, TPAs, and ancillary service coordinators. Authorization determinations are made by the applicable payer, adjuster, case manager, or TPA — being a network vendor does not automatically guarantee that a trip is covered or payable.
              </p>
              <Link to="/healthcare-partners/workers-compensation" className="inline-flex items-center text-sm font-semibold text-[#F9A825] hover:text-[#E08E1E]" data-testid="missouri-city-cta-workers-comp">
                Workers&apos; Compensation Transportation Details <ArrowRight className="ml-1 w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Nearby city links */}
      <section className="py-14 bg-gray-50" data-testid="missouri-city-nearby">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">NEARBY SERVICE AREAS</p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-4 leading-tight">
            Missouri City Sits Inside Our Regular Regional Coverage
          </h2>
          <p className="text-gray-700 mb-8 max-w-3xl">
            Cross-city medical trips are common for Missouri City patients. Explore location-specific pages for each neighboring service area:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {NEARBY_LINKS.map((n) => (
              <Link key={n.name} to={n.to} className="group" data-testid={`missouri-city-nearby-${n.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <Card className="h-full border-2 hover:border-[#F9A825] transition-all">
                  <CardHeader>
                    <div className="w-10 h-10 rounded-lg bg-[#F9A825] flex items-center justify-center mb-2">
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                    <CardTitle className="text-[#004C97] text-base leading-tight">{n.name}, TX</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 leading-relaxed mb-2">{n.note}</p>
                    <span className="text-sm font-semibold text-[#F9A825] group-hover:text-[#E08E1E]">View page →</span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Areas Served */}
      <section className="py-14 bg-white" data-testid="missouri-city-areas-served">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">AREAS SERVED</p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-4 leading-tight">
            Missouri City Neighborhoods &amp; Corridor Coverage
          </h2>
          <p className="text-gray-700 mb-6 max-w-3xl">
            Missouri City neighborhoods, master-planned communities, and cross-county corridors within our regular service area. Service is scheduled subject to availability. Extended-distance assignments may require advance authorization.
          </p>
          <div className="flex flex-wrap gap-2">
            {MC_AREAS.map((a) => (
              <span key={a} className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-full text-sm text-[#004C97] font-medium">
                {a}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose */}
      <section className="py-14 bg-gray-50" data-testid="missouri-city-why">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">WHY CHOOSE MOVERACARE</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97]">Missouri City NEMT, Coordinated From Next Door</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: MapPin, title: 'Adjacent to Our Home Base', desc: 'MoveraCare dispatches out of Richmond — Missouri City sits directly next door in Fort Bend County.' },
              { icon: Route, title: 'Two-County Coverage', desc: 'Fort Bend County core coverage plus supported Harris County service across the Missouri City line.' },
              { icon: Accessibility, title: 'Wheelchair-Accessible Vehicles', desc: 'Wheelchair-accessible vehicles with proper wheelchair securement. Drivers assist passengers during transport.' },
              { icon: Users, title: 'Driver Assistance', desc: 'Drivers assist passengers during transport. Mobility and equipment needs are confirmed at booking.' },
              { icon: Repeat, title: 'Standing Schedules', desc: 'Reliable recurring transportation for Missouri City dialysis, therapy, and treatment programs.' },
              { icon: Building2, title: 'Credentialing-Ready', desc: 'Licensing, insurance, and vendor documentation available for Missouri City facility procurement teams.' },
            ].map((w) => {
              const Icon = w.icon;
              return (
                <div key={w.title} className="flex items-start gap-3 p-5 bg-white border-2 border-gray-100 rounded-xl hover:border-[#F9A825] transition-all">
                  <div className="w-10 h-10 rounded-lg bg-[#F9A825]/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-[#F9A825]" />
                  </div>
                  <div>
                    <h3 className="text-[#004C97] font-semibold mb-1 leading-tight">{w.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{w.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 bg-white" data-testid="missouri-city-faq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">MISSOURI CITY FAQ</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97]">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {MC_FAQS.map((f) => (
              <details key={f.question} className="bg-gray-50 border-2 border-gray-100 rounded-lg p-5 group">
                <summary className="font-semibold text-[#004C97] cursor-pointer flex items-center justify-between">
                  <span>{f.question}</span>
                  <span className="text-[#F9A825] transition-transform group-open:rotate-90">→</span>
                </summary>
                <p className="mt-3 text-sm text-gray-700 leading-relaxed">{f.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 bg-[#F9A825]" data-testid="missouri-city-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-4">Request Missouri City Medical Transportation</h2>
          <p className="text-lg text-[#004C97]/85 mb-8">Book a Missouri City wheelchair or ambulatory ride, refer a patient, or reach dispatch directly.</p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-center">
            <Link to="/book">
              <Button size="lg" className="bg-[#004C97] text-white hover:bg-[#003D7A] font-semibold text-lg px-8 py-6 shadow-lg" data-testid="missouri-city-final-book">Book a Ride<ArrowRight className="ml-2 w-5 h-5" /></Button>
            </Link>
            <Link to="/request-patient-transportation">
              <Button size="lg" className="bg-white text-[#004C97] hover:bg-gray-100 font-semibold text-lg px-8 py-6 shadow-lg" data-testid="missouri-city-final-refer">Refer a Patient<ArrowRight className="ml-2 w-5 h-5" /></Button>
            </Link>
            <a href="tel:+18329252128">
              <Button size="lg" variant="outline" className="border-2 border-[#004C97] text-[#004C97] hover:bg-[#004C97] hover:text-white font-semibold text-lg px-8 py-6" data-testid="missouri-city-final-call"><Phone className="mr-2 w-5 h-5" />(832) 925-2128</Button>
            </a>
          </div>
        </div>
      </section>

      {/* Non-emergency disclaimer */}
      <section className="py-6 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-100 border-l-4 border-gray-400 rounded p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-700 leading-relaxed">
              MoveraCare provides <strong>non-emergency medical transportation</strong> for medically stable passengers in Missouri City and the surrounding Fort Bend and Harris County areas. We do not provide ambulance, 911, emergency response, stretcher ambulance, oxygen therapy, or clinical monitoring services. <strong>Not for medical emergencies. Call 911 in an emergency.</strong>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MissouriCityMedicalTransportation;
